import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Compass,
  FileText,
  Flame,
  Gauge,
  Loader2,
  MapIcon,
  Play,
  RefreshCcw,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import HighlightableText from "@/components/HighlightableText";

const CATEGORY_LABEL = {
  foundations: "Foundations",
  within: "Within",
  beyond: "Beyond",
  skills: "Skills",
};

const CATEGORY_COLOR = {
  foundations: "#4C6085",
  within: "#6B7FA3",
  beyond: "#B85C5C",
  skills: "#E88D67",
};

const MODE_META = {
  learn:       { difficulty: null,         description: "12-unit curriculum with worked examples and knowledge checks." },
  guided:      { difficulty: "intermediate", description: "Paragraph mapping + hints + after-question coaching." },
  untimed:     { difficulty: "intermediate", description: "One passage, no clock. Focus on reasoning." },
  timed:       { difficulty: "intermediate", description: "Visible 10-minute timer per passage. No hints." },
  weakness:    { difficulty: "intermediate", description: "Engine targets your weakest reasoning category." },
  fifty_fifty: { difficulty: "intermediate", description: "After your first guess, two wrong answers are hidden." },
  simulation:  { difficulty: null,         description: "9 passages, 53 questions, 90 minutes. No hints." },
  review:      { difficulty: null,         description: "Replay every question you've missed, newest first." },
  daily:       { difficulty: null,         description: "Three passages mixing categories and difficulty." },
};


// Acceptance harness (Part 19) — silent, logs to browser console only.
// Call `?qa=1` on the CARS Academy URL to trigger. Covers:
//  - passage QC at beginner / intermediate / advanced,
//  - session summary endpoint shape,
//  - error-log + retry drill,
//  - dashboard update, confidence tracking, and 50/50 tracker.
async function runAcademyAcceptance() {
  const results = [];
  const check = (name, pass, extra) => {
    results.push({ name, pass, extra });
    const tag = pass ? "✓" : "✗";
    const fn = pass ? console.log : console.warn;
    fn(`[CARS QA] ${tag} ${name}`, extra || "");
  };
  console.group("[CARS QA] Running acceptance tests…");
  try {
    // 1. Passage QC per difficulty.
    for (const diff of ["beginner", "intermediate", "advanced"]) {
      try {
        const r = await api.post("/cars-academy/passages/generate", { difficulty: diff, from_cache_ok: true });
        const p = r.data;
        const okCount = p.questions?.length >= 4 && p.questions?.length <= 7;
        const okChoices = (p.questions || []).every((q) => q.choices?.length === 4 && new Set(q.choices).size === 4);
        const okCats = ["foundations", "within", "beyond"].every((c) => (p.questions || []).some((q) => q.category === c));
        const okEvidence = (p.questions || []).every((q) => (q.evidence_quote || "").trim().length > 0);
        check(`passage QC: ${diff}`, okCount && okChoices && okCats && okEvidence,
              { title: p.title, questions: p.questions?.length });
      } catch (e) {
        check(`passage QC: ${diff}`, false, e.message);
      }
    }
    // 2. Session + confidence + 50/50 round trip.
    try {
      const start = await api.post("/cars-academy/sessions", { mode: "fifty_fifty", difficulty: "beginner" });
      const sid = start.data.session.id;
      const q0 = start.data.passage.questions[0];
      const a0 = await api.post(`/cars-academy/sessions/${sid}/answer`, {
        question_idx: 0,
        choice_idx: q0.answer_idx,
        confidence: "sure",
        time_sec: 25,
        fifty_fifty: { eliminated: [1, 2], first_choice: q0.answer_idx },
      });
      check("session answer round-trip", a0.data.correct === true, { sid });
      await api.post(`/cars-academy/sessions/${sid}/finish`, { timer_sec: 60 });
      const sum = await api.get(`/cars-academy/sessions/${sid}/summary`);
      check("session summary present", Boolean(sum.data?.next_action), {
        tip: sum.data.tip, next: sum.data.next_action,
      });
    } catch (e) {
      check("session round trip", false, e.message);
    }
    // 3. Dashboard + 50/50 tracker + confidence calibration.
    try {
      const d = await api.get("/cars-academy/dashboard");
      check("dashboard shape", typeof d.data.overall_accuracy === "number",
            { by_cat_keys: Object.keys(d.data.by_category || {}) });
      check("50/50 tracker records", (d.data.fifty_fifty?.total ?? 0) >= 0, d.data.fifty_fifty);
      check("confidence calibration tracks Sure", (d.data.calibration?.sure?.n ?? 0) >= 0, d.data.calibration?.sure);
    } catch (e) {
      check("dashboard", false, e.message);
    }
    // 4. Error log + retry drill.
    try {
      const log = await api.get("/cars-academy/error-log");
      check("error log endpoint", Array.isArray(log.data.items), { count: log.data.items.length });
      if (log.data.items.length) {
        const retry = await api.post("/cars-academy/error-log/retry", { count: 3 });
        check("retry drill creates a session", Boolean(retry.data?.session?.id),
              { qs: retry.data.passage.questions.length });
      } else {
        check("retry drill (skipped: no logged errors yet)", true);
      }
    } catch (e) {
      check("error log + retry", false, e.message);
    }
  } finally {
    const passed = results.filter((r) => r.pass).length;
    console.log(`[CARS QA] ${passed}/${results.length} checks passed`);
    console.groupEnd();
    window.__carsAcceptanceResults = results;
  }
}


export default function CarsAcademy() {
  const [view, setView] = useState({ name: "hub" });
  const [building, setBuilding] = useState(null);
  const [units, setUnits] = useState([]);
  const [modes, setModes] = useState([]);
  const [stats, setStats] = useState(null);
  const [focus, setFocus] = useState(null);
  const [a11y, setA11y] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pm_cars_a11y")) || { font: "medium", contrast: false };
    } catch { return { font: "medium", contrast: false }; }
  });

  useEffect(() => {
    try { localStorage.setItem("pm_cars_a11y", JSON.stringify(a11y)); } catch {}
  }, [a11y]);

  const load = async () => {
    try {
      const [u, s, f] = await Promise.all([
        api.get("/cars-academy/units"),
        api.get("/cars-academy/stats"),
        api.get("/cars-academy/focus"),
      ]);
      setUnits(u.data.units);
      setModes(u.data.modes);
      setStats(s.data);
      setFocus(f.data);
    } catch (e) {
      // ignore — surfaced in UI empty states
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Acceptance / health harness. Silent for students — logs only to console
  // and only when the user explicitly adds `?qa=1` to the URL. Used to
  // verify Parts 10-19 end-to-end without surfacing errors in-app.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("qa") !== "1") return;
    runAcademyAcceptance().catch((e) => console.warn("[CARS QA] harness crashed", e));
  }, []);

  if (view.name === "unit") {
    return <UnitView unitId={view.unitId} a11y={a11y} setA11y={setA11y} onBack={() => { setView({ name: "hub" }); load(); }} />;
  }
  if (view.name === "session") {
    return (
      <>
        <BuildingOverlay info={building} />
        <SessionView
          key={view.sessionId}
          sessionId={view.sessionId}
          passage={view.passage}
          session={view.session}
          mode={view.mode}
          a11y={a11y}
          setA11y={setA11y}
          onBack={() => { setView({ name: "hub" }); load(); }}
          onOpenUnit={(unitId) => setView({ name: "unit", unitId })}
          onStartMode={(mode) => startSession({ mode, setView, setBuilding })}
        />
      </>
    );
  }
  if (view.name === "simulation") {
    return <SimulationView a11y={a11y} setA11y={setA11y} onBack={() => { setView({ name: "hub" }); load(); }} />;
  }
  if (view.name === "review") {
    return <ReviewView onBack={() => { setView({ name: "hub" }); load(); }} setView={setView} />;
  }
  if (view.name === "dashboard") {
    return <DashboardView onBack={() => setView({ name: "hub" })} />;
  }
  if (view.name === "review-session") {
    return <SessionReviewView sessionId={view.sessionId} onBack={() => { setView({ name: "hub" }); load(); }} />;
  }

  return (
    <div data-testid="cars-academy-hub">
      <BuildingOverlay info={building} />
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-bold">CARS Academy</h1>
          <p className="text-base mt-1" style={{ color: "var(--text-secondary)" }}>
            A beginner-to-advanced CARS program built around the three AAMC reasoning categories.
          </p>
        </div>
        <button
          onClick={() => setView({ name: "dashboard" })}
          className="pm-btn-secondary flex items-center gap-2"
          data-testid="cars-open-dashboard"
        >
          <BarChart3 size={16} /> Open Performance Dashboard
        </button>
      </div>

      {focus && <FocusCard focus={focus} onOpenUnit={(id) => setView({ name: "unit", unitId: id })} />}

      <ProgressStrip stats={stats} />

      <h2 className="font-display font-bold text-lg mt-10 mb-3 flex items-center gap-2">
        <Compass size={18} /> Practice modes
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cars-modes-grid">
        {modes.map((m) => (
          <ModeCard
            key={m.id}
            mode={m}
            onPick={async () => {
              if (m.id === "learn") {
                document.getElementById("cars-units-grid")?.scrollIntoView({ behavior: "smooth" });
                return;
              }
              if (m.id === "review") {
                setView({ name: "review" });
                return;
              }
              if (m.id === "simulation") {
                setView({ name: "simulation" });
                return;
              }
              if (m.id === "daily") {
                try {
                  const r = await api.get("/cars-academy/daily");
                  const first = r.data.passages?.[0];
                  if (!first) {
                    toast.info("Daily set is being built — try again in a minute.");
                    return;
                  }
                  await startSession({ mode: "daily", passage_id: first.id, setView, setBuilding });
                } catch (e) {
                  toast.error("Couldn't load daily set.");
                }
                return;
              }
              await startSession({ mode: m.id, setView, setBuilding });
            }}
          />
        ))}
      </div>

      <MyPassagesStrip onOpen={(sessionId) => setView({ name: "review-session", sessionId })} />

      <h2 id="cars-units-grid" className="font-display font-bold text-lg mt-12 mb-3 flex items-center gap-2">
        <BookOpen size={18} /> 12-unit curriculum
      </h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cars-units-grid-list">
        {units.map((u) => (
          <UnitCard key={u.id} unit={u} onOpen={() => setView({ name: "unit", unitId: u.id })} />
        ))}
      </div>
    </div>
  );
}


const BUILD_STEPS = [
  "Choosing a humanities or social-sciences passage theme…",
  "Writing an original CARS passage at MCAT register…",
  "Drafting four-choice questions across foundations, within and beyond…",
  "Fact-checking every answer and refining the distractors…",
  "Running the quality-control pass…",
];

async function startSession({ mode, passage_id, setView, setBuilding }) {
  setBuilding?.({ mode, step: 0 });
  let step = 0;
  const tick = setInterval(() => { step = Math.min(step + 1, BUILD_STEPS.length - 1); setBuilding?.({ mode, step }); }, 7000);
  try {
    const payload = { mode };
    if (passage_id) payload.passage_id = passage_id;
    if (MODE_META[mode]?.difficulty) payload.difficulty = MODE_META[mode].difficulty;
    const r = await api.post("/cars-academy/sessions", payload);
    clearInterval(tick);
    setBuilding?.(null);
    setView({
      name: "session",
      sessionId: r.data.session.id,
      passage: r.data.passage,
      session: r.data.session,
      mode,
    });
  } catch (e) {
    clearInterval(tick);
    setBuilding?.(null);
    toast.error("Couldn't start a session — try again shortly.");
  }
}

function BuildingOverlay({ info }) {
  if (!info) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(10, 15, 30, 0.72)" }} data-testid="cars-building-overlay">
      <div className="pm-card p-8 max-w-md w-full text-center">
        <Loader2 size={32} className="animate-spin mx-auto mb-4" style={{ color: "var(--primary)" }} />
        <div className="font-display text-xl font-bold mb-1">Building your passage…</div>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
          Original CARS passages are written and quality-checked on demand. This takes about 60–90 seconds — thanks for sticking with us.
        </p>
        <ol className="text-left text-xs space-y-2">
          {BUILD_STEPS.map((label, i) => {
            const state = i < info.step ? "done" : i === info.step ? "active" : "pending";
            return (
              <li key={i} className="flex items-start gap-2" data-testid={`cars-building-step-${i}`} data-state={state}>
                <span className="w-5 h-5 rounded-full inline-flex items-center justify-center shrink-0 font-bold" style={{
                  background: state === "done" ? "var(--mastery)" : state === "active" ? "var(--primary)" : "var(--muted)",
                  color: state === "pending" ? "var(--text-muted)" : "white",
                  fontSize: 11,
                }}>{state === "done" ? "✓" : i + 1}</span>
                <span style={{ color: state === "pending" ? "var(--text-muted)" : "var(--text)" }}>{label}</span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}


function A11yBar({ a11y, setA11y, testid = "cars-a11y-bar" }) {
  const sizes = [
    { id: "small", label: "A-", hint: "Small font" },
    { id: "medium", label: "A", hint: "Medium font" },
    { id: "large", label: "A+", hint: "Large font" },
  ];
  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      role="toolbar"
      aria-label="Reading preferences"
      data-testid={testid}
    >
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>Reading:</div>
      {sizes.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => setA11y({ ...a11y, font: s.id })}
          aria-pressed={a11y.font === s.id}
          aria-label={s.hint}
          className="text-xs px-3 py-1 rounded-full border"
          style={{
            borderColor: a11y.font === s.id ? "var(--primary)" : "var(--border)",
            background: a11y.font === s.id ? "rgba(76,96,133,.08)" : "transparent",
            color: a11y.font === s.id ? "var(--primary)" : "var(--text-secondary)",
          }}
          data-testid={`${testid}-size-${s.id}`}
        >
          {s.label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setA11y({ ...a11y, contrast: !a11y.contrast })}
        aria-pressed={a11y.contrast}
        aria-label="Toggle high contrast reading mode"
        className="text-xs px-3 py-1 rounded-full border"
        style={{
          borderColor: a11y.contrast ? "var(--primary)" : "var(--border)",
          background: a11y.contrast ? "rgba(76,96,133,.08)" : "transparent",
          color: a11y.contrast ? "var(--primary)" : "var(--text-secondary)",
        }}
        data-testid={`${testid}-contrast`}
      >
        High contrast {a11y.contrast ? "on" : "off"}
      </button>
    </div>
  );
}

const PASSAGE_FONT = {
  small: "text-sm",
  medium: "text-base",
  large: "text-lg",
};

const HI_CONTRAST_STYLE = {
  background: "#ffffff",
  color: "#000000",
  border: "2px solid #000000",
};



function FocusCard({ focus, onOpenUnit }) {
  if (!focus?.ready) {
    return (
      <div className="pm-card p-4 flex items-center gap-3 mb-5" data-testid="cars-focus-card">
        <Sparkles size={18} style={{ color: "var(--accent)" }} />
        <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
          {focus?.message || "Finish three practice passages to unlock your personalized focus area."}
        </div>
      </div>
    );
  }
  return (
    <div
      className="pm-card p-5 mb-5"
      style={{ borderColor: focus.needs_remediation ? "var(--weakness)" : "var(--primary)", borderWidth: 2 }}
      data-testid="cars-focus-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <Target size={18} style={{ color: focus.needs_remediation ? "var(--weakness)" : "var(--primary)" }} />
        <div className="font-display font-bold text-lg">Your focus area this week</div>
      </div>
      <div className="text-sm mb-3" data-testid="cars-focus-message">{focus.message}</div>
      {focus.drill_hint && (
        <div className="text-xs italic mb-3" style={{ color: "var(--text-muted)" }}>
          Drill tip: {focus.drill_hint}
        </div>
      )}
      {focus.recommended_unit_id && (
        <button
          onClick={() => onOpenUnit(focus.recommended_unit_id)}
          className="pm-btn-primary"
          data-testid="cars-focus-open-unit"
        >
          Open {focus.recommended_unit_title}
        </button>
      )}
    </div>
  );
}


function ProgressStrip({ stats }) {
  if (!stats) return <div className="pm-card p-5 text-sm" style={{ color: "var(--text-muted)" }}>Loading progress…</div>;
  const cats = ["foundations", "within", "beyond"];
  return (
    <div className="pm-card p-5" data-testid="cars-progress-strip">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile
          icon={<BookOpen size={16} />}
          label="Units done"
          value={`${stats.units_done}/${stats.total_units}`}
          testid="stat-units"
        />
        <StatTile
          icon={<Trophy size={16} />}
          label="Practice sessions"
          value={stats.total_sessions}
          testid="stat-sessions"
        />
        {cats.map((c) => {
          const b = stats.by_category?.[c];
          return (
            <StatTile
              key={c}
              icon={<Target size={16} color={CATEGORY_COLOR[c]} />}
              label={CATEGORY_LABEL[c]}
              value={b ? `${b.accuracy}%` : "—"}
              hint={b ? `${b.correct}/${b.total}` : "no data yet"}
              testid={`stat-${c}`}
            />
          );
        })}
      </div>
    </div>
  );
}

function StatTile({ icon, label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>
        {icon} {label}
      </div>
      <div className="font-display text-2xl font-bold">{value}</div>
      {hint && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{hint}</div>}
    </div>
  );
}


function ModeCard({ mode, onPick }) {
  return (
    <button
      onClick={onPick}
      className="pm-card pm-card-hover text-left p-5"
      data-testid={`cars-mode-${mode.id}`}
    >
      <div className="text-2xl mb-2">{mode.icon}</div>
      <div className="font-display font-bold text-base mb-1">{mode.label}</div>
      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{mode.description}</div>
      <div className="mt-3 text-xs font-medium flex items-center gap-1" style={{ color: "var(--primary)" }}>
        Start <ChevronRight size={14} />
      </div>
    </button>
  );
}


function UnitCard({ unit, onOpen }) {
  const done = unit.lesson_complete;
  const color = CATEGORY_COLOR[unit.category];
  return (
    <button
      onClick={onOpen}
      className="pm-card pm-card-hover text-left p-5 relative"
      data-testid={`cars-unit-${unit.id}`}
    >
      {done && (
        <div
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs font-bold"
          style={{ color: "var(--mastery)" }}
          data-testid={`cars-unit-${unit.id}-done`}
        >
          <CheckCircle2 size={14} /> Done
        </div>
      )}
      <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color }}>
        Unit {unit.order} · {CATEGORY_LABEL[unit.category]}
      </div>
      <div className="font-display font-bold text-base mb-1">{unit.title}</div>
      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{unit.overview}</div>
    </button>
  );
}


// ======================================================================
//   UNIT VIEW
// ======================================================================

function UnitView({ unitId, a11y = { font: "medium", contrast: false }, setA11y = () => {}, onBack }) {
  const [unit, setUnit] = useState(null);
  const [picked, setPicked] = useState({});
  const [submitted, setSubmitted] = useState(null); // { kc_score, kc_total }

  useEffect(() => {
    api.get(`/cars-academy/units/${unitId}`).then((r) => setUnit(r.data)).catch(() => {});
  }, [unitId]);

  if (!unit) return <div className="p-6"><Loader2 className="animate-spin" /></div>;

  const submit = async () => {
    try {
      const r = await api.post(`/cars-academy/units/${unitId}/complete`, { knowledge_check: picked });
      setSubmitted(r.data);
      toast.success(`Scored ${r.data.kc_score}/${r.data.kc_total}`);
    } catch (e) {
      toast.error("Couldn't save your answers.");
    }
  };

  return (
    <div data-testid={`cars-unit-view-${unitId}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <button onClick={onBack} className="text-sm underline inline-flex items-center gap-1" style={{ color: "var(--primary)" }} data-testid="cars-unit-back">
          <ChevronLeft size={14} /> Back to Academy
        </button>
        <A11yBar a11y={a11y} setA11y={setA11y} testid="cars-unit-a11y" />
      </div>

      <div className={`${PASSAGE_FONT[a11y.font] || "text-base"}`} style={a11y.contrast ? HI_CONTRAST_STYLE : undefined}>
      <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: CATEGORY_COLOR[unit.category] }}>
        Unit {unit.order} · {CATEGORY_LABEL[unit.category]}
      </div>
      <h1 className="font-display text-3xl font-bold mb-2">{unit.title}</h1>
      <p className="text-base mb-6" style={{ color: "var(--text-secondary)" }}>{unit.overview}</p>

      <div className="pm-card p-5 mb-5">
        <div className="font-display font-bold text-sm uppercase tracking-wide mb-3" style={{ color: "var(--text-muted)" }}>Learning objectives</div>
        <ul className="space-y-1">
          {unit.objectives?.map((o, i) => (
            <li key={i} className="text-sm flex gap-2" data-testid={`unit-${unitId}-obj-${i}`}>
              <Target size={14} style={{ color: "var(--primary)", flexShrink: 0, marginTop: 3 }} />
              <span>{o}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="space-y-4 mb-6">
        {unit.lesson?.map((s, i) => (
          <div key={i} className="pm-card p-5" data-testid={`unit-${unitId}-lesson-${i}`}>
            <div className="font-display font-bold text-base mb-2">{s.heading}</div>
            <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.body}</div>
          </div>
        ))}
      </div>

      {unit.worked_example && (
        <div className="pm-card p-5 mb-6" style={{ background: "var(--muted)" }} data-testid={`unit-${unitId}-worked`}>
          <div className="font-display font-bold text-sm uppercase tracking-wide mb-3" style={{ color: "var(--text-muted)" }}>Worked example</div>
          <div className="text-sm italic mb-3" style={{ color: "var(--text-secondary)" }}>"{unit.worked_example.passage}"</div>
          <div className="font-medium text-sm mb-2">Q: {unit.worked_example.question}</div>
          <div className="text-sm">
            <span className="font-bold" style={{ color: "var(--mastery)" }}>Answer:</span> {unit.worked_example.answer}
          </div>
          <div className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{unit.worked_example.why}</div>
        </div>
      )}

      {unit.knowledge_check?.length > 0 && (
        <div className="pm-card p-5 mb-6" data-testid={`unit-${unitId}-kc`}>
          <div className="font-display font-bold text-sm uppercase tracking-wide mb-3" style={{ color: "var(--text-muted)" }}>Knowledge check</div>
          <div className="space-y-5">
            {unit.knowledge_check.map((q, qi) => {
              const chosen = picked[qi];
              return (
                <div key={qi} data-testid={`unit-${unitId}-kc-${qi}`}>
                  <div className="font-medium text-sm mb-2">{qi + 1}. {q.stem}</div>
                  <div className="space-y-1.5">
                    {q.choices.map((c, ci) => {
                      const isPicked = chosen === ci;
                      const isCorrect = submitted && ci === q.answer_idx;
                      const isWrongPick = submitted && isPicked && ci !== q.answer_idx;
                      return (
                        <button
                          key={ci}
                          onClick={() => !submitted && setPicked({ ...picked, [qi]: ci })}
                          className="w-full text-left text-sm px-3 py-2 rounded-xl border transition-colors"
                          style={{
                            borderColor: isCorrect ? "var(--mastery)" : isWrongPick ? "var(--weakness)" : (isPicked ? "var(--primary)" : "var(--border)"),
                            background: isCorrect ? "rgba(76,160,118,.08)" : isWrongPick ? "rgba(184,92,92,.08)" : "transparent",
                          }}
                          data-testid={`unit-${unitId}-kc-${qi}-c-${ci}`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                  {submitted && (
                    <div className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>{q.explain}</div>
                  )}
                </div>
              );
            })}
          </div>
          {!submitted && (
            <button
              onClick={submit}
              disabled={Object.keys(picked).length < unit.knowledge_check.length}
              className="mt-5 pm-btn-primary"
              data-testid={`unit-${unitId}-kc-submit`}
            >
              Mark unit complete
            </button>
          )}
          {submitted && (
            <div className="mt-5 text-sm" data-testid={`unit-${unitId}-kc-result`}>
              Scored <b>{submitted.kc_score}/{submitted.kc_total}</b>. Unit saved.
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}


// ======================================================================
//   SESSION VIEW (practice + guided + timed + 50/50 + weakness + daily)
// ======================================================================

function SessionView({ sessionId, passage: initPassage, session: initSession, mode, a11y = { font: "medium", contrast: false }, setA11y = () => {}, onBack, onOpenUnit, onStartMode }) {
  const [passage, setPassage] = useState(initPassage);
  const [session, setSession] = useState(initSession);
  const [stage, setStage] = useState(mode === "guided" ? "map" : "read");
  const [answers, setAnswers] = useState({});    // {qi: {choice, confidence, fifty_fifty}}
  const [feedback, setFeedback] = useState({});
  const [timer, setTimer] = useState(0);
  const [qStart, setQStart] = useState(() => Date.now());
  const [confidence, setConfidence] = useState({}); // {qi: "sure"|"unsure"|"guessing"}
  const [firstGuess, setFirstGuess] = useState({}); // 50/50: first choice before narrowing
  const [showMap, setShowMap] = useState(mode === "guided");
  const [mapInputs, setMapInputs] = useState(() => (passage?.paragraphs || []).map((_, i) => ({
    idx: i, main_point: "", function: "", author_position: "", transition: "", connection: "",
  })));
  const [mapFeedback, setMapFeedback] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const timed = mode === "timed" || mode === "simulation";
  const fiftyFifty = mode === "fifty_fifty";

  useEffect(() => {
    if (!timed) return;
    const t = setInterval(() => setTimer((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [timed]);

  const submitAnswer = async (qidx, cidx) => {
    const timeSec = Math.max(1, Math.round((Date.now() - qStart) / 1000));
    // 50/50 flow: first click records the guess + eliminates two wrong choices.
    // Second click records the final answer.
    const payload = {
      question_idx: qidx,
      choice_idx: cidx,
      confidence: confidence[qidx] || null,
      time_sec: timeSec,
    };
    if (fiftyFifty && firstGuess[qidx] !== undefined) {
      const q = passage.questions[qidx];
      const wrongIdxs = q.choices.map((_, i) => i).filter((i) => i !== q.answer_idx && i !== firstGuess[qidx]);
      payload.fifty_fifty = {
        eliminated: wrongIdxs.slice(0, 2),
        first_choice: firstGuess[qidx],
      };
    }
    try {
      const r = await api.post(`/cars-academy/sessions/${sessionId}/answer`, payload);
      setAnswers((a) => ({ ...a, [qidx]: cidx }));
      setFeedback((f) => ({ ...f, [qidx]: r.data }));
      setQStart(Date.now());
    } catch (e) {
      toast.error("Couldn't save your answer.");
    }
  };

  const recordFirstGuess = (qidx, cidx) => {
    // 50/50 mode: capture first guess, then let user re-pick between survivors.
    setFirstGuess((f) => ({ ...f, [qidx]: cidx }));
  };

  const finish = async () => {
    try {
      await api.post(`/cars-academy/sessions/${sessionId}/finish`, { timer_sec: timer });
      const sum = await api.get(`/cars-academy/sessions/${sessionId}/summary`);
      setSummary(sum.data);
      setStage("finished");
    } catch (e) {
      toast.error("Couldn't finish the session.");
    }
  };

  const submitMap = async () => {
    setMapLoading(true);
    try {
      const r = await api.post(`/cars-academy/sessions/${sessionId}/map`, { paragraphs: mapInputs });
      setMapFeedback(r.data);
    } catch (e) {
      toast.error("Couldn't score the map.");
    } finally {
      setMapLoading(false);
    }
  };

  if (!passage) return <div className="p-6"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="cars-session-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <button onClick={onBack} className="text-sm underline inline-flex items-center gap-1" style={{ color: "var(--primary)" }} data-testid="cars-session-back">
          <ChevronLeft size={14} /> Back
        </button>
        <A11yBar a11y={a11y} setA11y={setA11y} testid="cars-session-a11y" />
        {timed && <TimerBadge sec={timer} />}
      </div>

      <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>
        {MODE_META[mode]?.description || mode} · {passage.difficulty} · {passage.subject}
      </div>
      <h1 className="font-display text-2xl font-bold mb-4" data-testid="cars-session-title">{passage.title}</h1>

      {/* Passage with paragraph numbers + highlighter */}
      <article
        className={`pm-card p-6 mb-5 ${PASSAGE_FONT[a11y.font] || "text-base"}`}
        style={a11y.contrast ? HI_CONTRAST_STYLE : undefined}
        aria-label={`CARS passage: ${passage.title}`}
        data-testid="cars-session-passage"
      >
        {passage.paragraphs.map((p, i) => (
          <div key={i} className="mb-4 flex gap-3">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
              style={{ background: "var(--muted)", color: "var(--text-muted)" }}
              aria-hidden="true"
            >
              {i + 1}
            </div>
            <div className="flex-1 leading-relaxed" aria-label={`Paragraph ${i + 1}`}>
              <HighlightableText text={p} storageKey={`cars-session-${sessionId}-p${i}`} testid={`cars-passage-p-${i}`} />
            </div>
          </div>
        ))}
      </article>

      {(showMap || mode === "guided") && (
        <ParagraphMapper
          passage={passage}
          inputs={mapInputs}
          onChange={setMapInputs}
          onSubmit={submitMap}
          loading={mapLoading}
          feedback={mapFeedback}
          onDone={() => setStage("read")}
        />
      )}

      {!mapFeedback && mode === "guided" && stage === "map" ? null : (
        <>
          <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
            <FileText size={18} /> Questions
          </div>
          <div className="space-y-5">
            {passage.questions.map((q, qi) => (
              <QuestionBlock
                key={qi}
                qi={qi}
                q={q}
                picked={answers[qi]}
                feedback={feedback[qi]}
                confidence={confidence[qi]}
                onConfidence={(v) => setConfidence((c) => ({ ...c, [qi]: v }))}
                firstGuess={firstGuess[qi]}
                onFirstGuess={(ci) => recordFirstGuess(qi, ci)}
                onPick={(ci) => submitAnswer(qi, ci)}
                fiftyFifty={fiftyFifty}
                hintsOn={mode === "guided"}
              />
            ))}
          </div>

          {stage !== "finished" && (
            <div className="mt-6 pt-5 border-t flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "var(--border)" }} data-testid="cars-passage-nav">
              <button
                disabled
                title="Only one passage in this session"
                className="pm-btn-secondary inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="cars-prev-passage"
              >
                <ChevronLeft size={14} /> Back to previous passage
              </button>
              <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="cars-passage-counter">Passage 1 / 1</span>
              <button
                onClick={finish}
                disabled={Object.keys(answers).length === 0}
                className="pm-btn-primary inline-flex items-center gap-1.5"
                data-testid="cars-session-finish"
              >
                Finish &amp; see summary <ChevronRight size={14} />
              </button>
            </div>
          )}

          {stage === "finished" && summary && (
            <SessionSummary summary={summary} timer={timer} mode={mode} onOpenUnit={onOpenUnit} onStartMode={onStartMode} onBack={onBack} />
          )}
        </>
      )}
    </div>
  );
}


function SessionSummary({ summary, timer, mode, onOpenUnit, onStartMode, onBack }) {
  const next = summary.next_action;
  return (
    <div className="pm-card p-6 mt-6" data-testid="cars-session-summary">
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={18} style={{ color: "var(--primary)" }} />
        <div className="font-display font-bold text-xl">Session summary</div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-4">
        <SummaryStat label="Score" value={`${summary.correct}/${summary.total}`} testid="sum-score" />
        <SummaryStat label="Accuracy" value={`${summary.accuracy}%`} testid="sum-accuracy" />
        <SummaryStat label="Avg time / Q" value={summary.avg_time_sec ? `${summary.avg_time_sec}s` : "—"} testid="sum-time" />
        <SummaryStat
          label="Confidence calibration"
          value={summary.calibration_score == null ? "—" : `${summary.calibration_score}%`}
          hint="when you said 'Sure'"
          testid="sum-calibration"
        />
      </div>

      <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Accuracy by reasoning category</div>
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(summary.by_category || {}).map(([cat, v]) => (
          <div
            key={cat}
            className="text-xs px-3 py-1 rounded-full"
            style={{ background: "var(--muted)", color: CATEGORY_COLOR[cat] || "var(--text)" }}
            data-testid={`sum-cat-${cat}`}
          >
            {CATEGORY_LABEL[cat] || cat}: {v.correct}/{v.total}
          </div>
        ))}
      </div>

      {summary.top_error && (
        <div className="text-sm mb-2" data-testid="sum-top-error">
          <b>Top error pattern:</b> "{summary.top_error.qtype.replace(/_/g, " ")}" ({summary.top_error.count} miss{summary.top_error.count > 1 ? "es" : ""} this session)
        </div>
      )}
      {summary.tip && (
        <div className="text-sm italic mb-4" style={{ color: "var(--text-secondary)" }} data-testid="sum-tip">
          Tip: {summary.tip}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
        <button
          onClick={() => onStartMode?.(mode || "untimed")}
          className="pm-btn-primary flex items-center gap-2"
          data-testid="cars-summary-new-passage"
        >
          <Sparkles size={14} /> Generate a new passage
        </button>
        <button
          onClick={onBack}
          className="pm-btn-secondary flex items-center gap-2"
          data-testid="cars-summary-return-home"
        >
          <ChevronLeft size={14} /> Return to CARS home
        </button>
        {next && (
          <button
            onClick={() => {
              if (next.kind === "unit") onOpenUnit?.(next.value);
              else if (next.kind === "mode") onStartMode?.(next.value);
            }}
            className="pm-btn-secondary flex items-center gap-2"
            data-testid="sum-next-action"
          >
            {next.label}
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, hint, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-display text-xl font-bold">{value}</div>
      {hint && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{hint}</div>}
    </div>
  );
}

function TimerBadge({ sec }) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  const srLabel = `Time elapsed: ${Math.floor(sec / 60)} minutes ${sec % 60} seconds`;
  return (
    <div
      className="pm-badge flex items-center gap-1"
      style={{ background: "var(--primary)", color: "white" }}
      role="timer"
      aria-live="polite"
      aria-label={srLabel}
      data-testid="cars-timer"
    >
      <Clock size={12} aria-hidden="true" /> <span aria-hidden="true">{m}:{s}</span>
      <span className="sr-only">{srLabel}</span>
    </div>
  );
}


function QuestionBlock({ qi, q, picked, feedback, confidence, onConfidence, firstGuess, onFirstGuess, onPick, fiftyFifty, hintsOn }) {
  const answered = feedback !== undefined && feedback !== null;
  // 50/50: after the first guess is captured, hide two wrong choices so the
  // student picks between the correct answer and one remaining distractor.
  const hidden = useMemo(() => {
    if (!fiftyFifty || firstGuess === undefined || answered) return [];
    const wrongIdxs = q.choices.map((_, i) => i).filter((i) => i !== q.answer_idx && i !== firstGuess);
    return wrongIdxs.slice(0, 2);
  }, [fiftyFifty, firstGuess, answered, q.choices, q.answer_idx]);

  const handlePick = (ci) => {
    if (answered) return;
    if (fiftyFifty && firstGuess === undefined) {
      onFirstGuess(ci);
      return;
    }
    onPick(ci);
  };

  return (
    <div className="pm-card p-5" data-testid={`cars-q-${qi}`}>
      <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: CATEGORY_COLOR[q.category] }}>
        {CATEGORY_LABEL[q.category] || q.category} · {q.qtype}
      </div>
      <div className="font-medium text-sm mb-3">{qi + 1}. {q.stem}</div>

      {fiftyFifty && firstGuess !== undefined && !answered && (
        <div
          className="text-xs mb-3 p-2 rounded-lg"
          style={{ background: "rgba(76,96,133,.08)", color: "var(--primary)" }}
          data-testid={`cars-q-${qi}-5050-note`}
        >
          Two wrong answers hidden. First guess was <b>{String.fromCharCode(65 + firstGuess)}</b> — commit now.
        </div>
      )}

      <div className="space-y-1.5">
        {q.choices.map((c, ci) => {
          if (hidden.includes(ci)) return null;
          const isPicked = picked === ci;
          const isCorrect = feedback && ci === feedback.answer_idx;
          const isWrongPick = feedback && isPicked && !feedback.correct;
          const isFirstGuess = fiftyFifty && firstGuess === ci && !answered;
          return (
            <button
              key={ci}
              onClick={() => handlePick(ci)}
              disabled={answered}
              className="w-full text-left text-sm px-3 py-2 rounded-xl border transition-colors"
              style={{
                borderColor: isCorrect ? "var(--mastery)" : isWrongPick ? "var(--weakness)" : (isPicked ? "var(--primary)" : isFirstGuess ? "var(--accent)" : "var(--border)"),
                background: isCorrect ? "rgba(76,160,118,.08)" : isWrongPick ? "rgba(184,92,92,.08)" : isFirstGuess ? "rgba(232,141,103,.08)" : "transparent",
                cursor: answered ? "default" : "pointer",
              }}
              data-testid={`cars-q-${qi}-c-${ci}`}
            >
              <b className="mr-2">{String.fromCharCode(65 + ci)}.</b> {c}
            </button>
          );
        })}
      </div>

      {!answered && (
        <ConfidenceRow value={confidence} onChange={onConfidence} qi={qi} />
      )}

      {hintsOn && !answered && (
        <HintPane evidence={q.evidence_quote} />
      )}
      {feedback && (
        <AnswerExplainer qi={qi} q={q} feedback={feedback} picked={picked} />
      )}
    </div>
  );
}


function ConfidenceRow({ value, onChange, qi }) {
  const items = [
    { id: "sure", label: "Sure" },
    { id: "unsure", label: "Unsure" },
    { id: "guessing", label: "Guessing" },
  ];
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <div className="text-xs" style={{ color: "var(--text-muted)" }}>Confidence:</div>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          onClick={() => onChange(it.id)}
          className="text-xs px-3 py-1 rounded-full border"
          style={{
            borderColor: value === it.id ? "var(--primary)" : "var(--border)",
            background: value === it.id ? "rgba(76,96,133,.08)" : "transparent",
            color: value === it.id ? "var(--primary)" : "var(--text-secondary)",
          }}
          data-testid={`cars-q-${qi}-conf-${it.id}`}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

function HintPane({ evidence }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs underline"
        style={{ color: "var(--primary)" }}
        data-testid="cars-hint-toggle"
      >
        {open ? "Hide hint" : "Reveal hint"}
      </button>
      {open && (
        <div className="text-xs mt-2 italic p-2 rounded-lg" style={{ background: "var(--muted)", color: "var(--text-secondary)" }}>
          Look for: "{evidence}"
        </div>
      )}
    </div>
  );
}


function AnswerExplainer({ qi, q, feedback, picked }) {
  return (
    <div className="mt-4 p-4 rounded-xl" style={{ background: "var(--muted)" }} data-testid={`cars-q-${qi}-explain`}>
      <div className="flex items-center gap-2 mb-2">
        {feedback.correct ? (
          <CheckCircle2 size={16} style={{ color: "var(--mastery)" }} />
        ) : (
          <XCircle size={16} style={{ color: "var(--weakness)" }} />
        )}
        <div className="font-display font-bold text-sm">
          {feedback.correct ? "Correct" : "Not quite"} · answer: {String.fromCharCode(65 + feedback.answer_idx)}
        </div>
      </div>
      {feedback.evidence_quote && (
        <div className="text-xs italic mb-2" style={{ color: "var(--text-muted)" }}>
          Passage evidence: "{feedback.evidence_quote}"
        </div>
      )}
      <div className="text-sm mb-2">
        <b style={{ color: "var(--mastery)" }}>Why it's right:</b> {feedback.why_correct}
      </div>
      {feedback.why_wrong && (
        <div className="text-sm space-y-1">
          {q.choices.map((_, ci) => {
            if (ci === feedback.answer_idx) return null;
            const w = feedback.why_wrong[String(ci)];
            if (!w) return null;
            return (
              <div key={ci}>
                <b>{String.fromCharCode(65 + ci)}:</b> {w}
              </div>
            );
          })}
        </div>
      )}
      {!feedback.correct && (
        <div className="mt-3 text-xs" style={{ color: "var(--text-secondary)" }}>
          <div><b>Likely trap:</b> {feedback.reasoning_error_hint}</div>
          <div><b>Next time:</b> {feedback.improvement_tip}</div>
        </div>
      )}
    </div>
  );
}


// ======================================================================
//   PARAGRAPH MAPPER
// ======================================================================

function ParagraphMapper({ passage, inputs, onChange, onSubmit, loading, feedback, onDone }) {
  const fields = [
    { key: "main_point", label: "Main point" },
    { key: "function", label: "Function" },
    { key: "author_position", label: "Author's position" },
    { key: "transition", label: "Transition" },
    { key: "connection", label: "Link to main idea" },
  ];
  const upd = (i, k, v) => {
    const next = [...inputs];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  return (
    <div className="pm-card p-5 mb-5" data-testid="cars-paragraph-mapper">
      <div className="font-display font-bold text-lg mb-1 flex items-center gap-2">
        <MapIcon size={18} /> Paragraph mapper
      </div>
      <div className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
        Fill a one-line answer for each paragraph. The tutor compares your map with a reference — different phrasing is fine, drift in meaning is flagged.
      </div>
      <div className="space-y-4">
        {passage.paragraphs.map((_, i) => (
          <div key={i} className="rounded-xl border p-3" style={{ borderColor: "var(--border)" }} data-testid={`cars-map-p-${i}`}>
            <div className="font-bold text-xs uppercase tracking-wide mb-2">Paragraph {i + 1}</div>
            <div className="grid md:grid-cols-2 gap-2">
              {fields.map((f) => (
                <input
                  key={f.key}
                  value={inputs[i]?.[f.key] || ""}
                  onChange={(e) => upd(i, f.key, e.target.value)}
                  placeholder={f.label}
                  className="w-full text-sm px-3 py-2 rounded-lg border bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`cars-map-p-${i}-${f.key}`}
                />
              ))}
            </div>
            {feedback?.paragraphs?.find((x) => x.idx === i) && (
              <div
                className="text-xs mt-2 p-2 rounded-lg"
                style={{
                  background: "var(--muted)",
                  color: {
                    strong: "var(--mastery)",
                    partial: "var(--primary)",
                    miss: "var(--weakness)",
                  }[feedback.paragraphs.find((x) => x.idx === i).match] || "var(--text)",
                }}
                data-testid={`cars-map-p-${i}-feedback`}
              >
                <b>{feedback.paragraphs.find((x) => x.idx === i).match.toUpperCase()}</b> — {feedback.paragraphs.find((x) => x.idx === i).feedback}
              </div>
            )}
          </div>
        ))}
      </div>
      {!feedback ? (
        <button
          onClick={onSubmit}
          disabled={loading}
          className="pm-btn pm-btn-primary mt-4"
          data-testid="cars-map-submit"
        >
          {loading ? "Tutor reviewing…" : "Submit map"}
        </button>
      ) : (
        <div className="mt-4" data-testid="cars-map-feedback">
          <div className="text-sm mb-1">Overall match: <b>{feedback.feedback?.overall_match}%</b></div>
          <div className="text-sm italic" style={{ color: "var(--text-secondary)" }}>{feedback.feedback?.summary}</div>
          <button onClick={onDone} className="pm-btn pm-btn-primary mt-3" data-testid="cars-map-continue">
            Continue to questions
          </button>
        </div>
      )}
    </div>
  );
}


// ======================================================================
//   SIMULATION VIEW
// ======================================================================

function SimulationView({ a11y = { font: "medium", contrast: false }, setA11y = () => {}, onBack }) {
  const [loading, setLoading] = useState(true);
  const [passages, setPassages] = useState([]);
  const [sim, setSim] = useState(null);
  const [curr, setCurr] = useState(0);
  const [timer, setTimer] = useState(90 * 60); // 90 minutes down-count
  const [done, setDone] = useState(false);
  const [answers, setAnswers] = useState({}); // { "pid|qidx": cidx }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.post("/cars-academy/simulation/start");
        if (!mounted) return;
        setSim(r.data.simulation);
        setPassages(r.data.passages);
      } catch (e) {
        toast.error("Couldn't build the simulation.");
      }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (loading || done) return;
    const t = setInterval(() => {
      setTimer((s) => {
        if (s <= 1) {
          clearInterval(t);
          setDone(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [loading, done]);

  const total = useMemo(() => passages.reduce((n, p) => n + p.questions.length, 0), [passages]);
  const correct = useMemo(() => {
    let c = 0;
    for (const p of passages) {
      for (let qi = 0; qi < p.questions.length; qi++) {
        const a = answers[`${p.id}|${qi}`];
        if (a === p.questions[qi].answer_idx) c++;
      }
    }
    return c;
  }, [passages, answers]);

  if (loading) return <div className="p-6"><Loader2 className="animate-spin" /></div>;
  if (!passages.length) return <div className="p-6">No passages available. <button onClick={onBack} className="underline">Go back</button></div>;

  if (done) {
    return (
      <div data-testid="cars-sim-done">
        <button onClick={onBack} className="text-sm underline mb-4 inline-flex items-center gap-1" style={{ color: "var(--primary)" }}>
          <ChevronLeft size={14} /> Back
        </button>
        <h1 className="font-display text-3xl font-bold mb-2">Simulation complete</h1>
        <div className="pm-card p-5 mb-5">
          <div className="font-display text-2xl font-bold mb-1">Score: {correct}/{total}</div>
          <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
            {Math.round(100 * correct / Math.max(1, total))}% — equivalent percent correct on the real section.
          </div>
        </div>
      </div>
    );
  }

  const p = passages[curr];
  const mm = Math.floor(timer / 60).toString().padStart(2, "0");
  const ss = (timer % 60).toString().padStart(2, "0");

  return (
    <div data-testid="cars-sim-view">
      <div className="sticky top-0 z-10 flex items-center justify-between py-3 mb-4 flex-wrap gap-3" style={{ background: "var(--bg)" }}>
        <button onClick={onBack} className="text-sm underline inline-flex items-center gap-1" style={{ color: "var(--primary)" }}>
          <ChevronLeft size={14} /> Exit simulation
        </button>
        <A11yBar a11y={a11y} setA11y={setA11y} testid="cars-sim-a11y" />
        <div
          className="pm-badge flex items-center gap-1"
          style={{ background: timer < 300 ? "var(--weakness)" : "var(--primary)", color: "white" }}
          role="timer"
          aria-live="polite"
          aria-label={`Time remaining: ${Math.floor(timer / 60)} minutes ${timer % 60} seconds`}
          data-testid="cars-sim-timer"
        >
          <Clock size={12} aria-hidden="true" /> <span aria-hidden="true">{mm}:{ss}</span>
          <span className="sr-only">{`Time remaining: ${Math.floor(timer / 60)} minutes ${timer % 60} seconds`}</span>
        </div>
        <div className="text-xs" style={{ color: "var(--text-muted)" }}>Passage {curr + 1} / {passages.length}</div>
      </div>

      <h2 className="font-display text-xl font-bold mb-3">{p.title}</h2>
      <article
        className={`pm-card p-6 mb-5 ${PASSAGE_FONT[a11y.font] || "text-base"}`}
        style={a11y.contrast ? HI_CONTRAST_STYLE : undefined}
        aria-label={`CARS passage: ${p.title}`}
      >
        {p.paragraphs.map((text, i) => (
          <div key={i} className="mb-3" aria-label={`Paragraph ${i + 1}`}><HighlightableText text={text} storageKey={`sim-${sim?.id}-${p.id}-p${i}`} testid={`sim-passage-${curr}-p-${i}`} /></div>
        ))}
      </article>

      <div className="space-y-4 mb-5">
        {p.questions.map((q, qi) => {
          const key = `${p.id}|${qi}`;
          const picked = answers[key];
          return (
            <div key={qi} className="pm-card p-4" data-testid={`cars-sim-q-${curr}-${qi}`}>
              <div className="font-medium text-sm mb-2">{qi + 1}. {q.stem}</div>
              <div className="space-y-1.5">
                {q.choices.map((c, ci) => (
                  <button
                    key={ci}
                    onClick={() => setAnswers({ ...answers, [key]: ci })}
                    className="w-full text-left text-sm px-3 py-2 rounded-xl border"
                    style={{
                      borderColor: picked === ci ? "var(--primary)" : "var(--border)",
                      background: picked === ci ? "rgba(76,96,133,.08)" : "transparent",
                    }}
                    data-testid={`cars-sim-q-${curr}-${qi}-c-${ci}`}
                  >
                    <b className="mr-2">{String.fromCharCode(65 + ci)}.</b> {c}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={() => setCurr((c) => Math.max(0, c - 1))}
          disabled={curr === 0}
          className="pm-btn-secondary"
          data-testid="cars-sim-prev"
        >
          Previous passage
        </button>
        {curr < passages.length - 1 ? (
          <button onClick={() => setCurr((c) => c + 1)} className="pm-btn-primary" data-testid="cars-sim-next">
            Next passage
          </button>
        ) : (
          <button onClick={() => setDone(true)} className="pm-btn-primary" data-testid="cars-sim-submit">
            Submit simulation
          </button>
        )}
      </div>
    </div>
  );
}


// ======================================================================
//   ERROR LOG — filterable, with Retry drill
// ======================================================================

function MyPassagesStrip({ onOpen }) {
  const [passages, setPassages] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => { api.get("/cars-academy/my-passages").then((r) => setPassages(r.data.passages || [])).catch(() => setPassages([])); }, []);
  if (!passages || passages.length === 0) return null;
  const shown = expanded ? passages : passages.slice(0, 6);
  const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const fmtTime = (sec) => { if (!sec) return "—"; const m = Math.floor(sec / 60), x = sec % 60; return m ? `${m}m ${x}s` : `${x}s`; };
  return (
    <div className="mt-10" data-testid="cars-my-passages">
      <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Trophy size={18} /> My passages</h2>
      <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>Every CARS passage you've completed. Tap one to re-read it with your answers, the correct answers and the explanations.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((p) => (
          <button key={p.session_id} onClick={() => onOpen(p.session_id)}
            className="pm-card p-4 text-left transition-transform hover:-translate-y-0.5"
            data-testid={`cars-my-passage-${p.session_id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="pm-badge text-[10px]" style={{ background: "var(--muted)" }}>{p.difficulty}</span>
              <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>{p.subject}</span>
            </div>
            <div className="font-display font-bold text-base leading-snug">{p.title}</div>
            <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--text-muted)" }}>
              <span>{fmt(p.finished_at)} · {fmtTime(p.timer_sec)}</span>
              <span className="font-mono" style={{ color: p.accuracy >= 80 ? "var(--mastery)" : p.accuracy >= 60 ? "var(--primary)" : "var(--weakness)" }}>{p.correct}/{p.total}</span>
            </div>
          </button>
        ))}
      </div>
      {passages.length > 6 && (
        <button onClick={() => setExpanded(!expanded)} className="text-sm mt-3 hover:underline" style={{ color: "var(--primary)" }} data-testid="cars-my-passages-toggle">
          {expanded ? "Show less" : `Show all ${passages.length}`}
        </button>
      )}
    </div>
  );
}

function SessionReviewView({ sessionId, onBack }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/cars-academy/sessions/${sessionId}`).then((r) => setData(r.data)); }, [sessionId]);
  if (!data) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  const p = data.passage, sess = data.session;
  const answers = sess.answers || {};
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const total = p.questions.length;
  const correct = Object.values(answers).filter((a) => a.correct).length;
  return (
    <div data-testid="cars-session-review-view">
      <button onClick={onBack} className="text-sm underline inline-flex items-center gap-1 mb-4" style={{ color: "var(--primary)" }} data-testid="cars-review-back"><ChevronLeft size={14} /> CARS home</button>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{p.difficulty} · {p.subject} · completed {fmt(sess.finished_at)}</div>
          <h1 className="font-display text-3xl font-bold">{p.title}</h1>
        </div>
        <div className="font-mono text-2xl font-bold" style={{ color: "var(--primary)" }} data-testid="cars-review-score">{correct}/{total}</div>
      </div>
      <article className="pm-card p-6 mb-6" data-testid="cars-review-passage">
        {p.paragraphs.map((para, i) => (
          <div key={i} className="mb-4 flex gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: "var(--muted)", color: "var(--text-muted)" }}>{i + 1}</div>
            <div className="flex-1 leading-relaxed">{para}</div>
          </div>
        ))}
      </article>
      <div className="space-y-4">
        {p.questions.map((q, qi) => {
          const a = answers[String(qi)] || {};
          const picked = a.choice_idx;
          return (
            <div key={qi} className="pm-card p-5" data-testid={`cars-review-q-${qi}`}>
              <div className="text-xs font-bold uppercase tracking-wide mb-1" style={{ color: CATEGORY_COLOR[q.category] }}>{CATEGORY_LABEL[q.category] || q.category} · {q.qtype}</div>
              <div className="font-medium text-sm mb-3">{qi + 1}. {q.stem}</div>
              <div className="space-y-1.5 mb-3">
                {q.choices.map((c, ci) => {
                  const isCorrect = ci === q.answer_idx;
                  const isPicked = picked === ci;
                  return (
                    <div key={ci} className="text-sm px-3 py-2 rounded-xl border flex gap-2 items-start"
                      style={{ borderColor: isCorrect ? "var(--mastery)" : isPicked && !isCorrect ? "var(--weakness)" : "var(--border)",
                               background: isCorrect ? "rgba(76,160,118,.08)" : isPicked && !isCorrect ? "rgba(184,92,92,.08)" : "transparent" }}
                      data-testid={`cars-review-q-${qi}-c-${ci}`}>
                      <b>{String.fromCharCode(65 + ci)}.</b>
                      <span className="flex-1">{c}</span>
                      {isCorrect && <span className="pm-badge text-[10px]" style={{ background: "var(--mastery)", color: "white" }}>Correct</span>}
                      {isPicked && !isCorrect && <span className="pm-badge text-[10px]" style={{ background: "var(--weakness)", color: "white" }}>Your pick</span>}
                      {isPicked && isCorrect && <span className="pm-badge text-[10px]" style={{ background: "var(--primary)", color: "white" }}>Your pick</span>}
                    </div>
                  );
                })}
              </div>
              {picked == null && <p className="text-xs mb-2" style={{ color: "var(--weakness)" }}>You didn't answer this question.</p>}
              <div className="text-sm mb-1" data-testid={`cars-review-why-correct-${qi}`}><b>Why it's correct:</b> {q.why_correct}</div>
              {q.why_wrong && (
                <ul className="text-xs space-y-1 mt-2">
                  {q.choices.map((_, ci) => (ci !== q.answer_idx && (q.why_wrong[String(ci)] || q.why_wrong[ci])) ? (
                    <li key={ci} data-testid={`cars-review-why-wrong-${qi}-${ci}`}><b>{String.fromCharCode(65 + ci)}:</b> {q.why_wrong[String(ci)] || q.why_wrong[ci]}</li>
                  ) : null)}
                </ul>
              )}
              {q.improvement_tip && <p className="text-xs italic mt-2" style={{ color: "var(--text-secondary)" }}>Tip: {q.improvement_tip}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ReviewView({ onBack, setView }) {
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [qtypeCounts, setQtypeCounts] = useState({});
  const [retryBusy, setRetryBusy] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (category) params.category = category;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/cars-academy/error-log", { params });
      setItems(r.data.items);
      setQtypeCounts(r.data.qtype_counts || {});
    } catch (e) {
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, dateFrom, dateTo]);

  const retry = async () => {
    setRetryBusy(true);
    try {
      const r = await api.post("/cars-academy/error-log/retry", { category: category || null, count: 5 });
      setView({
        name: "session",
        sessionId: r.data.session.id,
        passage: r.data.passage,
        session: r.data.session,
        mode: "error_drill",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start a drill.");
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <div data-testid="cars-error-log-view">
      <button onClick={onBack} className="text-sm underline mb-4 inline-flex items-center gap-1" style={{ color: "var(--primary)" }} data-testid="cars-log-back">
        <ChevronLeft size={14} /> Back
      </button>
      <h1 className="font-display text-3xl font-bold mb-2">Error Log</h1>
      <p className="text-base mb-5" style={{ color: "var(--text-secondary)" }}>
        Every question you've missed. Filter by reasoning category or date, then run a focused Retry drill.
      </p>

      <div className="pm-card p-4 flex flex-wrap items-end gap-3 mb-5" data-testid="cars-log-filters">
        <FilterSelect label="Reasoning category" value={category} onChange={setCategory} testid="cars-log-category">
          <option value="">All categories</option>
          <option value="foundations">Foundations</option>
          <option value="within">Within</option>
          <option value="beyond">Beyond</option>
        </FilterSelect>
        <FilterDate label="From" value={dateFrom} onChange={setDateFrom} testid="cars-log-date-from" />
        <FilterDate label="To" value={dateTo} onChange={setDateTo} testid="cars-log-date-to" />
        <button
          onClick={retry}
          disabled={retryBusy || !items?.length}
          className="pm-btn-primary flex items-center gap-2 ml-auto"
          data-testid="cars-log-retry"
        >
          <RefreshCcw size={14} /> {retryBusy ? "Building drill…" : "Retry 5 errors"}
        </button>
      </div>

      {Object.keys(qtypeCounts).length > 0 && (
        <div className="pm-card p-4 mb-5" data-testid="cars-log-qtype-counts">
          <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--text-muted)" }}>Misses by question type</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(qtypeCounts).sort((a, b) => b[1] - a[1]).map(([qt, n]) => (
              <div
                key={qt}
                className="text-xs px-3 py-1 rounded-full"
                style={{ background: "var(--muted)", color: "var(--text-secondary)" }}
                data-testid={`cars-log-qtype-${qt}`}
              >
                {qt.replace(/_/g, " ")}: {n}
              </div>
            ))}
          </div>
        </div>
      )}

      {items === null && <Loader2 className="animate-spin" />}
      {items && items.length === 0 && (
        <div className="pm-card p-5 text-sm" style={{ color: "var(--text-muted)" }} data-testid="cars-log-empty">
          No missed questions in this view — finish a practice session or widen your filters.
        </div>
      )}
      {items && items.length > 0 && (
        <div className="space-y-4">
          {items.map((it, i) => (
            <div key={i} className="pm-card p-5" data-testid={`cars-log-item-${i}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <div className="text-xs font-bold uppercase tracking-wide" style={{ color: CATEGORY_COLOR[it.question.category] }}>
                  {CATEGORY_LABEL[it.question.category] || it.question.category}
                </div>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{it.passage_title}</div>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{it.difficulty}</div>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <div className="text-xs" style={{ color: "var(--text-muted)" }}>{it.finished_at?.slice(0, 10)}</div>
                <span style={{ color: "var(--text-muted)" }}>·</span>
                <div className="text-xs font-bold" style={{ color: "var(--weakness)" }}>
                  missed this type ×{it.qtype_miss_count}
                </div>
              </div>
              <div className="font-medium text-sm mb-3">{it.question.stem}</div>
              <div className="space-y-1 text-sm">
                {it.question.choices.map((c, ci) => (
                  <div
                    key={ci}
                    className="px-3 py-2 rounded-xl border"
                    style={{
                      borderColor: ci === it.question.answer_idx ? "var(--mastery)" : ci === it.student_choice ? "var(--weakness)" : "var(--border)",
                      background: ci === it.question.answer_idx ? "rgba(76,160,118,.08)" : ci === it.student_choice ? "rgba(184,92,92,.08)" : "transparent",
                    }}
                  >
                    <b>{String.fromCharCode(65 + ci)}.</b> {c}
                  </div>
                ))}
              </div>
              <div className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
                <b style={{ color: "var(--mastery)" }}>Why {String.fromCharCode(65 + it.question.answer_idx)} is right:</b> {it.question.why_correct}
              </div>
              <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                <b>Trap:</b> {it.question.reasoning_error_hint}. <b>Fix:</b> {it.question.improvement_tip}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, children, testid }) {
  return (
    <label className="text-xs flex flex-col gap-1" style={{ color: "var(--text-muted)" }}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm px-2 py-1 rounded-lg border bg-transparent"
        style={{ borderColor: "var(--border)" }}
        data-testid={testid}
      >
        {children}
      </select>
    </label>
  );
}

function FilterDate({ label, value, onChange, testid }) {
  return (
    <label className="text-xs flex flex-col gap-1" style={{ color: "var(--text-muted)" }}>
      {label}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm px-2 py-1 rounded-lg border bg-transparent"
        style={{ borderColor: "var(--border)" }}
        data-testid={testid}
      />
    </label>
  );
}


// ======================================================================
//   PERFORMANCE DASHBOARD
// ======================================================================

function DashboardView({ onBack }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/cars-academy/dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);
  if (!d) return <div className="p-6"><Loader2 className="animate-spin" /></div>;

  const catBars = ["foundations", "within", "beyond"].map((c) => ({
    name: CATEGORY_LABEL[c],
    acc: d.by_category?.[c]?.accuracy ?? 0,
    fill: CATEGORY_COLOR[c],
  }));

  return (
    <div data-testid="cars-dashboard-view">
      <button onClick={onBack} className="text-sm underline mb-4 inline-flex items-center gap-1" style={{ color: "var(--primary)" }}>
        <ChevronLeft size={14} /> Back
      </button>
      <h1 className="font-display text-3xl font-bold mb-2">CARS Performance Dashboard</h1>
      <p className="text-base mb-6" style={{ color: "var(--text-secondary)" }}>
        Updates in real time after every session.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="cars-dash-kpis">
        <KpiTile icon={<Trophy size={16} />} label="Overall accuracy" value={`${d.overall_accuracy}%`} testid="dash-overall" />
        <KpiTile icon={<FileText size={16} />} label="Passages done" value={d.total_passages} testid="dash-passages" />
        <KpiTile icon={<Target size={16} />} label="Questions answered" value={d.total_questions} testid="dash-questions" />
        <KpiTile
          icon={<Flame size={16} style={{ color: "var(--accent)" }} />}
          label="Current streak"
          value={`${d.streak_days} day${d.streak_days === 1 ? "" : "s"}`}
          testid="dash-streak"
        />
        <KpiTile
          icon={<Clock size={16} />}
          label="Avg time per Q"
          value={d.avg_time_sec ? `${d.avg_time_sec}s` : "—"}
          testid="dash-time"
        />
        <KpiTile
          icon={<Gauge size={16} />}
          label="Predicted CARS score"
          value={d.predicted_band?.estimate ? `${d.predicted_band.estimate}` : "—"}
          hint={d.predicted_band?.sample_too_small ? "sample too small" : `band ${d.predicted_band?.min}-${d.predicted_band?.max}`}
          testid="dash-band"
        />
        <KpiTile
          icon={<Target size={16} style={{ color: "var(--mastery)" }} />}
          label="Strongest unit"
          value={d.strongest_unit?.unit_id ? d.strongest_unit.unit_id.replace("unit-", "Unit ") : "—"}
          hint={d.strongest_unit ? `${d.strongest_unit.kc_score}/${d.strongest_unit.kc_total}` : null}
          testid="dash-strongest"
        />
        <KpiTile
          icon={<Target size={16} style={{ color: "var(--weakness)" }} />}
          label="Weakest unit"
          value={d.weakest_unit?.unit_id ? d.weakest_unit.unit_id.replace("unit-", "Unit ") : "—"}
          hint={d.weakest_unit ? `${d.weakest_unit.kc_score}/${d.weakest_unit.kc_total}` : null}
          testid="dash-weakest"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <div className="pm-card p-5" data-testid="cars-dash-cat-chart">
          <div className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <BarChart3 size={16} /> Accuracy by reasoning category
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={catBars}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="acc" radius={[6, 6, 0, 0]}>
                  {catBars.map((b, i) => (
                    <Bar key={i} fill={b.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="pm-card p-5" data-testid="cars-dash-trend-chart">
          <div className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <TrendingUp size={16} /> Accuracy trend
          </div>
          {d.trend.length === 0 ? (
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>Finish a few sessions to see your trend here.</div>
          ) : (
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={d.trend}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="accuracy" stroke="var(--primary)" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="pm-card p-5" data-testid="cars-dash-calibration">
          <div className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <Gauge size={16} /> Confidence calibration
          </div>
          <div className="space-y-2 text-sm">
            {["sure", "unsure", "guessing"].map((c) => {
              const row = d.calibration?.[c];
              return (
                <div key={c} className="flex items-center justify-between" data-testid={`cars-dash-conf-${c}`}>
                  <div className="capitalize">{c}</div>
                  <div style={{ color: "var(--text-secondary)" }}>
                    {row?.n ? `${row.accuracy}% correct · ${row.n} question${row.n === 1 ? "" : "s"}` : "no data yet"}
                  </div>
                </div>
              );
            })}
          </div>
          {d.calibration_pattern && (
            <div
              className="mt-3 text-xs p-2 rounded-lg"
              style={{
                background: d.calibration_pattern === "overconfident" ? "rgba(184,92,92,.08)" : "rgba(76,160,118,.08)",
                color: d.calibration_pattern === "overconfident" ? "var(--weakness)" : "var(--mastery)",
              }}
              data-testid="cars-dash-conf-tip"
            >
              <b>{d.calibration_pattern === "overconfident" ? "Overconfident pattern" : "Underconfident pattern"}:</b> {d.calibration_tip}
            </div>
          )}
        </div>

        <div className="pm-card p-5" data-testid="cars-dash-5050">
          <div className="font-display font-bold text-base mb-3 flex items-center gap-2">
            <RefreshCcw size={16} /> 50/50 strategy
          </div>
          {d.fifty_fifty?.total ? (
            <div className="text-sm space-y-1">
              <div><b>{d.fifty_fifty.accuracy}%</b> correct when you committed after narrowing.</div>
              <div style={{ color: "var(--text-muted)" }}>
                {d.fifty_fifty.correct} right out of {d.fifty_fifty.total} tries.
              </div>
            </div>
          ) : (
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>
              Run a 50/50 Challenge session and your pick-rate will show up here.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiTile({ icon, label, value, hint, testid }) {
  return (
    <div className="pm-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>
        {icon} {label}
      </div>
      <div className="font-display text-xl font-bold">{value}</div>
      {hint && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{hint}</div>}
    </div>
  );
}
